import type { SubmitTransition } from '@funkit/connect-core'
import type { ReactElement } from 'react'
import { useCallback, useEffect, useRef, useState } from 'react'

import { logger } from '../../logging/logger'
import { useFunkitTranslation } from '../../providers/FunkitI18nProvider'
import type { StripeOnrampClient } from './stripeOnramp'
import { StripeSessionAuthScreen } from './StripeSessionAuthScreen'

/** Keeps a non-Error rejection readable without assuming its shape. */
import { describeThrown } from './describeThrown'

// Re-exported from its old home so existing importers keep working.
export { describeThrown }

export function StripeSessionAuthController({
  stripe,
  verifiedEmail,
  transition,
  pendingFallback,
  autoStart,
  onResume,
  onSubmit,
  onClose,
}: {
  stripe: StripeOnrampClient
  verifiedEmail: string
  transition: SubmitTransition
  /**
   * Drawn while the mount-time `/fiat/session` post is in flight. This round
   * asks the buyer for nothing, so a screen of its own would be a loading
   * surface they did not ask for: the caller passes the screen they were
   * already on and keeps its action pending instead.
   */
  pendingFallback: ReactElement
  /**
   * Whether arriving here is the buyer's own doing. False once they have dismissed Stripe's sheet:
   * this component unmounts while that sheet owns the screen, so `preflightStarted` cannot outlive
   * the round trip, and starting again on the remount reopens the sheet they just closed.
   */
  autoStart: boolean
  /** The buyer asking for the round again, after declining it. */
  onResume?: () => void
  onSubmit: (transition: SubmitTransition) => Promise<void>
  onClose: () => void
}) {
  const { t } = useFunkitTranslation()
  // The backend transition starts on mount. Begin in the matching visual state
  // so the first paint cannot flash an empty authentication shell — but only when it really is
  // starting, or a declined round would sit on the pending fallback with nothing in flight.
  const [isSubmitting, setIsSubmitting] = useState(autoStart)
  const [errorMessage, setErrorMessage] = useState<string>()
  const inFlight = useRef(false)
  const mounted = useRef(true)
  // Keyed on the transition, not a bare boolean. The page identity does not
  // change between SESSION_AUTH rounds, so a second round found the guard
  // already set and never started — leaving the buyer on a filled form whose
  // Create account re-registered an account that now exists.
  const preflightStarted = useRef<string | undefined>(undefined)

  // React StrictMode runs an effect cleanup before re-running its setup in a
  // development build. Restore the guard in setup so the Link-account lookup
  // from the second mount can settle instead of leaving the auth shell empty.
  useEffect(() => {
    mounted.current = true
    return () => {
      mounted.current = false
    }
  }, [])

  const run = useCallback(
    async (task: () => Promise<void>, fallbackMessage: string) => {
      if (inFlight.current) {
        return
      }
      inFlight.current = true
      setIsSubmitting(true)
      setErrorMessage(undefined)
      try {
        await task()
      } catch (error) {
        logger.warn('connect-rn:fiat:stripe_auth_preflight_failed', {
          // The SDK rejects with a plain `{ code, message }` rather than an Error, which
          // `String(error)` flattens to "[object Object]" and tells nobody anything.
          error: describeThrown(error),
        })
        if (mounted.current) {
          setErrorMessage(fallbackMessage)
        }
      } finally {
        inFlight.current = false
        if (mounted.current) {
          setIsSubmitting(false)
        }
      }
    },
    [],
  )

  const startBackendAuth = useCallback(async () => {
    logger.info('connect-rn:fiat:stripe_session_auth_started')
    await onSubmit(transition)
  }, [onSubmit, transition])

  /**
   * Diagnostic only, and deliberately not awaited.
   *
   * The backend decides whether this buyer has to register: a session it cannot
   * place a Link account for comes back INPUT_REQUIRED/ACCOUNT_CREATION, so a
   * SESSION_AUTH standing already asserts one exists. This controller used to
   * form its own verdict from `hasLinkAccount` and register on it — a second,
   * backend-unaware way to register, which fired `registerLinkUser` against the
   * account the account-creation round had created seconds earlier and failed
   * with "There is already a user ... in network LINK".
   *
   * The lookup is kept because that disagreement is worth seeing, and dropped
   * from the critical path because nothing may act on it. If the two really do
   * disagree, `authorize` fails next and says so.
   */
  const reportLinkAccountState = useCallback(() => {
    void stripe
      .hasLinkAccount(verifiedEmail)
      .then((result) => {
        if (!mounted.current) {
          return
        }
        if (result.error !== undefined) {
          logger.warn('connect-rn:fiat:stripe_link_lookup_failed', {
            code: result.error.code,
            onrampErrorType: result.error.onrampErrorType,
            developerMessage: result.error.developerMessage,
            requestId: result.error.requestId,
          })
          return
        }
        logger.info('connect-rn:fiat:stripe_link_account_checked', {
          exists: result.hasLinkAccount,
        })
        if (!result.hasLinkAccount) {
          // The backend said this buyer is registered and Stripe's client says
          // otherwise. Registering here is what caused the loop; name it instead.
          logger.warn(
            'connect-rn:fiat:stripe_link_account_missing_at_session_auth',
          )
        }
      })
      // Detached on purpose, so a rejection here has nowhere to surface. Without
      // this it became an unhandled rejection rather than the diagnostic line
      // this whole lookup exists to produce.
      .catch((error: unknown) => {
        logger.warn('connect-rn:fiat:stripe_link_lookup_threw', {
          error: describeThrown(error),
        })
      })
  }, [stripe, verifiedEmail])

  const continueAuth = useCallback(
    () => void run(startBackendAuth, t('fiat.stripeAuthFailed')),
    [run, startBackendAuth, t],
  )

  // Entering this executor is the result of the user's SESSION_AUTH CTA tap;
  // start its local prerequisite without adding a second confirmation tap.
  useEffect(() => {
    if (!autoStart || preflightStarted.current === transition.id) {
      return
    }
    preflightStarted.current = transition.id
    reportLinkAccountState()
    continueAuth()
  }, [autoStart, continueAuth, reportLinkAccountState, transition.id])

  if (isSubmitting && errorMessage === undefined) {
    return pendingFallback
  }

  return (
    <StripeSessionAuthScreen
      verifiedEmail={verifiedEmail}
      isSubmitting={isSubmitting}
      errorMessage={errorMessage}
      onContinue={() => {
        onResume?.()
        continueAuth()
      }}
      onClose={onClose}
    />
  )
}
